/**
 * @fileoverview CMS Nest: fetches linked pages and replaces [vdx-nest-link] with content from [vdx-nest-this] on the target page.
 * Uses throttled fetches (concurrency cap + delay) to avoid rate limits; cache (TTL) and URL deduplication reduce requests.
 */

import { functions } from './functions.js';
import { logError, logWarn, showErrorOnElement } from './errors.js';

const MODULE = 'cmsnest';

/** localStorage key prefix for cached page HTML. */
const CACHE_KEY_PREFIX = 'vdx-nest:';

/** Cache TTL in ms. Cached HTML older than this will be refetched. Reduces repeat hits on Webflow. */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** Max concurrent fetches; start next when one completes. */
const FETCH_CONCURRENCY = 3;

/** Delay in ms before starting each new fetch; spaces requests to reduce rate-limit risk. */
const FETCH_DELAY_MS = 100;

/** Selector for links that trigger nest-in of remote content. */
const SELECTOR_NEST_LINK = '[vdx-nest-link]';

/** Attribute on the link whose value identifies the [vdx-nest-this] fragment on the target page. */
const NEST_LINK_ATTR = 'vdx-nest-link';

/** Attribute name on the target page that identifies the fragment to nest (value matches vdx-nest-link). */
const NEST_THIS_ATTR = 'vdx-nest-this';

/**
 * Returns the cache key for a URL.
 * @param {string} url
 * @returns {string}
 */
function _cacheKey(url) {
    return CACHE_KEY_PREFIX + url;
}

/**
 * Reads cached HTML and timestamp from localStorage. Returns null if missing or invalid.
 * @param {string} url
 * @returns {{ html: string, ts: number } | null}
 */
function _getCached(url) {
    if (typeof localStorage === 'undefined') return null;
    try {
        const raw = localStorage.getItem(_cacheKey(url));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.html !== 'string' || typeof parsed?.ts !== 'number') return null;
        return { html: parsed.html, ts: parsed.ts };
    } catch {
        return null;
    }
}

/**
 * Writes HTML and current timestamp to localStorage for the given URL.
 * @param {string} url
 * @param {string} html
 */
function _setCached(url, html) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(_cacheKey(url), JSON.stringify({ html, ts: Date.now() }));
    } catch {
        // quota or disabled
    }
}

/**
 * Fetches a URL and returns the response text. Throws on non-ok or network error.
 * @param {string} url
 * @returns {Promise<string>}
 */
async function _fetchHtml(url) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    return response.text();
}

/**
 * Resolves after ms. Used to throttle fetch starts.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Returns HTML for the URL: from cache if within TTL, otherwise fetches and caches.
 * @param {string} url
 * @returns {Promise<string>}
 */
async function _getCachedOrFetch(url) {
    const cached = _getCached(url);
    if (cached && (Date.now() - cached.ts) < CACHE_TTL_MS) {
        return cached.html;
    }
    const html = await _fetchHtml(url);
    _setCached(url, html);
    return html;
}

/**
 * Fetches URLs with concurrency cap and delay: at most FETCH_CONCURRENCY in flight,
 * FETCH_DELAY_MS before starting each. Cache (TTL) reduces repeat fetches.
 * @param {string[]} urls - Unique URLs to fetch.
 * @returns {Promise<Map<string, string>>} Map of url -> html.
 */
async function _fetchAllThrottled(urls) {
    const valid = urls.filter((u) => u && typeof u === 'string');
    const map = new Map();
    let next = 0;

    const runOne = async () => {
        const i = next++;
        if (i >= valid.length) return;
        const url = valid[i];
        if (i > 0) await _delay(FETCH_DELAY_MS);
        try {
            const html = await _getCachedOrFetch(url);
            map.set(url, html);
        } catch (error) {
            logError(`Failed to fetch nest URL: ${url}`, { module: MODULE, error });
        }
        if (next < valid.length) await runOne();
    };

    const workers = Array(Math.min(FETCH_CONCURRENCY, valid.length)).fill(null).map(() => runOne());
    await Promise.all(workers);
    return map;
}

/**
 * Re-initializes Webflow scripts after DOM updates (e.g. after nest replacement).
 */
function _addWebflowScripts() {
    try {
        if (typeof Webflow === 'undefined') return;
        Webflow.destroy();
        Webflow.ready();
        if (Webflow.require && typeof Webflow.require('ix2')?.init === 'function') {
            Webflow.require('ix2').init();
        }
    } catch (error) {
        logError('Webflow scripts failed to load. Ensure Webflow is available and vdx-nest-link / cmsNest integration is correct.', { module: MODULE, error });
    }
}

/**
 * Shows the "nest target not found" error UI on the link (border, tooltip, showErrorOnElement).
 * Preserves existing behavior for developers.
 * @param {Element} link - The element with vdx-nest-link.
 * @param {string} nestValue - Value of vdx-nest-link.
 */
function _showNestNotFoundError(link, nestValue) {
    const msg = `vdx-nest-link value "${nestValue}" has no matching [vdx-nest-this="${nestValue}"] in the linked page. Check spelling and that the target exists.`;
    logWarn(msg, { module: MODULE });
    showErrorOnElement(link, msg);
    link.style.border = '2px solid red';
    const tooltip = document.createElement('div');
    tooltip.textContent = `[vdx-nest-this='${nestValue}'] does not have a corresponding [vdx-next-this='${nestValue}'] attribute`;
    tooltip.style.position = 'absolute';
    tooltip.style.top = '100%';
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translateX(-50%)';
    tooltip.style.backgroundColor = 'red';
    tooltip.style.color = 'white';
    tooltip.style.padding = '5px';
    tooltip.style.borderRadius = '5px';
    tooltip.style.zIndex = '1000';
    if (link.parentNode) {
        link.parentNode.insertBefore(tooltip, link.nextSibling);
    }
}

/**
 * Applies nested content for one link: parses html, finds [vdx-nest-this=value], replaces link with that element's outerHTML.
 * @param {Element} link - Element with vdx-nest-link.
 * @param {string} html - Full page HTML from the link's href.
 * @returns {'replaced' | 'not_found' | 'no_parent'}
 */
function _applyNestedContent(link, html) {
    const nestValue = link.getAttribute(NEST_LINK_ATTR);
    if (!nestValue || !html) {
        return 'not_found';
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const nestedElement = doc.querySelector(`[vdx-nest-this='${nestValue}']`);
    if (!nestedElement) {
        return 'not_found';
    }
    if (!link.parentNode) {
        return 'no_parent';
    }
    link.outerHTML = nestedElement.outerHTML;
    return 'replaced';
}

/**
 * Main entry: finds all [vdx-nest-link], fetches each unique URL once (throttled, cached with TTL),
 * then replaces each link with the corresponding [vdx-nest-this] fragment. On success, re-runs functions and Webflow scripts.
 */
export const cmsNest = async () => {
    const nestLinks = Array.from(document.querySelectorAll(SELECTOR_NEST_LINK));
    if (nestLinks.length === 0) return;

    const uniqueUrls = [...new Set(nestLinks.map((link) => link.href).filter(Boolean))];
    const urlToHtml = await _fetchAllThrottled(uniqueUrls);

    for (let i = 0; i < nestLinks.length; i++) {
        const link = nestLinks[i];
        const html = urlToHtml.get(link.href);
        const nestValue = link.getAttribute(NEST_LINK_ATTR);

        if (!html) {
            logError(`No HTML available for nest link href: ${link.href}`, { module: MODULE });
            continue;
        }

        const result = _applyNestedContent(link, html);

        if (result === 'replaced') {
            continue;
        }

        if (result === 'not_found') {
            _showNestNotFoundError(link, nestValue ?? '');
            // Process remaining links (replace them) then exit without calling functions/addWebflowScripts
            const remainingLinks = nestLinks.slice(i + 1);
            for (const other of remainingLinks) {
                const otherHtml = urlToHtml.get(other.href);
                if (otherHtml) {
                    _applyNestedContent(other, otherHtml);
                }
            }
            return;
        }

        if (result === 'no_parent') {
            const msg = 'vdx-nest-link element has no parent (cannot replace). Ensure the link is in the DOM with a parent element.';
            logWarn(msg, { module: MODULE });
            showErrorOnElement(link, msg);
            return;
        }
    }

    functions();
    _addWebflowScripts();
};
